import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { type Range, type RangeKeyDict } from 'react-date-range';
import { Button } from '@/components/common/Button';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import { Formatter } from '@/core/utils/Formatter';
import { type DateRange } from '@/types';
import styles from './TimeControlRangePicker.module.scss';
import { useCustomRangePresets } from './hooks/useCustomRangePresets';

export interface TimeControlRangePickerProps {
  onClose?: () => void;
  applyFilters?: (dateRange: DateRange) => void;
  selectedDateRange?: DateRange[];
  defaultSelection?: 'week' | 'month' | 'quarter' | 'year';
}

const getDefaultSelection = (type: TimeControlRangePickerProps['defaultSelection']): Range => {
  const today = new Date();
  switch (type) {
    case 'week':
      return { startDate: startOfWeek(today), endDate: endOfWeek(today) };
    case 'month':
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
    case 'quarter':
      return { startDate: startOfQuarter(today), endDate: endOfQuarter(today) };
    case 'year':
      return { startDate: startOfYear(today), endDate: endOfYear(today) };
    default:
      return { startDate: undefined, endDate: undefined };
  }
};

export const TimeControlRangePicker: React.FC<TimeControlRangePickerProps> = ({
  selectedDateRange: [{ from, to }] = [{ from: undefined, to: undefined }],
  onClose,
  applyFilters,
  defaultSelection = 'week',
}) => {
  const customRanges = useCustomRangePresets();
  const defaultRange = getDefaultSelection(defaultSelection);
  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: from ? Formatter.toUTC(from) : defaultRange.startDate,
      endDate: to ? Formatter.toUTC(to) : defaultRange.endDate,
      key: 'selection',
    },
  ]);

  const handleDatePickerChange = useCallback((range: RangeKeyDict) => setDateRange([range.selection]), []);

  const handleFiltersApply = useCallback(() => {
    const [{ startDate, endDate }] = dateRange;

    if (!startDate || !endDate) return;

    applyFilters?.({
      from: Formatter.toFilterDateTime(startOfDay(startDate)),
      to: Formatter.toFilterDateTime(endOfDay(endDate)),
    });

    onClose?.();
  }, [dateRange]);

  return (
    <div className={styles.root}>
      <DateRangePicker
        classNames={{
          dateDisplayItem: styles.rdrDateDisplayItem,
          dateDisplayItemActive: styles.rdrDateDisplayItemActive,
          yearPicker: styles.rdrPicker,
          monthPicker: styles.rdrPicker,
        }}
        onClose={onClose}
        ranges={dateRange}
        staticRanges={customRanges}
        inputRanges={[]}
        onChange={handleDatePickerChange}
        fixedHeight
        editableDateInputs
        moveRangeOnFirstSelection={false}
      />
      <div className={styles.footer}>
        <Button className={classnames(styles.button, styles.cancel)} onClick={onClose}>
          Cancel
        </Button>
        <Button className={styles.button} type="submit" onClick={handleFiltersApply}>
          Save
        </Button>
      </div>
    </div>
  );
};
export default TimeControlRangePicker;
